import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { smartLinkPlatformDomains } from 'fym-shared/src/smartlinks/domains';
import { TypedMemo } from '../../../common/types';
import styles from './SMLDomainSelect.module.scss';

interface SMLDomainSelectProps {
  value: string;
  onChange: (domain: string) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * The host part of a page address, as a control rather than a label.
 *
 * A native select, because the whole thing has to sit inside one line of an
 * address and stay as narrow as the host it shows. It is only ever rendered
 * beside a prefix that carries a platform host, so the list is always the
 * complete set of choices.
 */
const SMLDomainSelectComponent: React.FC<SMLDomainSelectProps> = ({ value, onChange, disabled, className }) => {
  const intl = useIntl();

  const onSelectChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => onChange(event.target.value),
    [onChange]
  );

  const domains = useMemo(() => [...smartLinkPlatformDomains], []);

  return (
    <select
      className={clsx(styles.select, className)}
      value={value}
      disabled={disabled}
      onChange={onSelectChange}
      aria-label={intl.formatMessage({ id: 'app.sml.domain.label', defaultMessage: 'Page domain' })}
    >
      {domains.map((domain) => (
        <option key={domain} value={domain}>
          {domain}
        </option>
      ))}
    </select>
  );
};

export const SMLDomainSelect = TypedMemo(SMLDomainSelectComponent);
